<script lang="ts">
	import { onDestroy, createEventDispatcher } from "svelte";
	import ModalCSS from "./ModalCSS";

	const dispatch = createEventDispatcher();

	let inClass = false;
	let display = false;
	let isShown = false;
	let modalCSS = new ModalCSS();

	// Elements
	let _backdrop;
	let _modal;
	let _dialog;
	let _dismiss;

	// BS variables
	let originalBodyPad = null;
	let scrollbarWidth = 0;
	let bodyIsOverflowing: boolean;
	let ignoreBackdropClick: boolean | string = false;
	let fixedContent = ".navbar-fixed-top, .navbar-fixed-bottom";

	export let title = "Default Modal Title";
	export let fade = true;
	export const controls = {
		toggle: function (e) {
			isShown ? onHide() : onShow();
		},
		show: onShow,
		hide: onHide,
		handleUpdate: adjustDialog,
	};
	export let options: any = {};

	options = Object.assign(
		{
			backdrop: true,
			keyboard: true,
			size: "",
		},
		options
	);

	// Private functions

	function onShow() {
		dispatch("show.bs.modal");

		if (isShown) {
			return;
		}

		isShown = true;

		checkScrollbar();
		setScrollbar();
		document.body.classList.add("modal-open");

		resize();

		backdrop(async () => {
			modalCSS.display = "block";
			modalCSS = modalCSS;
			_modal.scroll(0, 0);

			adjustDialog();

			if (fade) {
				_modal.offsetWidth; // force reflow
			}

			await new Promise((resolve) => setTimeout(resolve, 1));
			inClass = true;

			enforceFocus();

			if (fade) {
				oneOff(_dialog, "transitionend", () => {
					dispatch("shown.bs.modal", { relatedTarget: _modal });
					_modal.focus();
				});
			} else {
				dispatch("shown.bs.modal", { relatedTarget: _modal });
				_modal.focus();
			}
		});
	}

	function onHide() {
		dispatch("hide.bs.modal");

		if (!isShown) {
			return;
		}

		isShown = false;

		resize();

		document.addEventListener("focusin", onFocusin);

		inClass = false;

		fade ? oneOff(_modal, "transitionend", hideModal) : hideModal();
	}

	function enforceFocus() {
		document.removeEventListener("focusin", onFocusin); // guard against infinite focus loop
		document.addEventListener("focusin", onFocusin);
	}

	function resize() {
		if (isShown) {
			window.addEventListener("resize", adjustDialog);
		} else {
			window.removeEventListener("resize", adjustDialog);
		}
	}

	function hideModal() {
		modalCSS.display = null;

		backdrop(() => {
			document.body.classList.remove("modal-open");
			resetAdjustments();
			resetScrollbar();

			dispatch("hidden.bs.modal");
		});
	}

	function removeBackdrop() {
		_backdrop && _backdrop.remove();
		_backdrop = null;
	}

	function backdrop(callback) {
		if (isShown && options.backdrop) {
			_backdrop = document.createElement("div");
			_backdrop.classList.add("modal-backdrop");

			if (fade) {
				_backdrop.classList.add("fade");
			}

			document.body.append(_backdrop);

			if (fade) {
				_backdrop.offsetWidth; // force reflow
			}

			_backdrop.classList.add("in");

			if (!callback) {
				return;
			}

			fade ? oneOff(_backdrop, "transitionend", callback) : callback();
		} else if (!isShown && _backdrop) {
			_backdrop.classList.remove("in");

			const callbackRemove = () => {
				removeBackdrop();
				callback && callback();
			};

			fade
				? oneOff(_backdrop, "transitionend", callbackRemove)
				: callbackRemove();
		} else if (callback) {
			callback();
		}
	}

	function adjustDialog() {
		const modalIsOverflowing =
			_modal.scrollHeight > document.documentElement.clientHeight;

		modalCSS.paddingLeft =
			!bodyIsOverflowing && modalIsOverflowing
				? scrollbarWidth + "px"
				: null;
		modalCSS.paddingRight =
			bodyIsOverflowing && !modalIsOverflowing
				? scrollbarWidth + "px"
				: null;

		modalCSS = modalCSS;
	}

	function resetAdjustments() {
		modalCSS.paddingLeft = null;
		modalCSS.paddingRight = null;
	}

	function checkScrollbar() {
		let fullWindowWidth = window.innerWidth;

		bodyIsOverflowing = document.body.clientWidth < fullWindowWidth;
		scrollbarWidth = measureScrollbar();
	}

	function setScrollbar() {
		const bodyPad = parseInt(
			getComputedStyle(document.body).getPropertyValue("padding-right") ||
				"0",
			10
		);

		originalBodyPad = document.body.style.paddingRight || "";

		if (bodyIsOverflowing) {
			document.body.style.paddingRight = bodyPad + scrollbarWidth + "px";

			document
				.querySelectorAll(fixedContent)
				.forEach((element: HTMLElement) => {
					const actualPadding = element.style.paddingRight;
					const calculatedPadding =
						getComputedStyle(element).getPropertyValue(
							"padding-right"
						);

					element.dataset.paddingRight = actualPadding;
					element.style.paddingRight = `${
						parseFloat(calculatedPadding) + scrollbarWidth
					}px`;
				});
		}
	}

	function resetScrollbar() {
		document.body.style.paddingRight = originalBodyPad;

		document
			.querySelectorAll(fixedContent)
			.forEach((element: HTMLElement) => {
				const padding = element.dataset.paddingRight;

				delete element.dataset.paddingRight;

				element.style.paddingRight = padding ? padding : "";
			});
	}

	function measureScrollbar() {
		const scrollDiv = document.createElement("div");

		scrollDiv.classList.add("modal-scrollbar-measure");
		document.body.append(scrollDiv);

		const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

		scrollDiv.remove();

		return scrollbarWidth;
	}

	// Event functions

	function onFocusin({ target }) {
		if (
			document !== target &&
			_modal !== target &&
			!_modal.contains(target)
		) {
			_modal.focus();
		}
	}

	function onKeydown(e: KeyboardEvent) {
		if (isShown && options.keyboard && e.key === "Escape") {
			e.preventDefault();
			controls.hide();
		}
	}

	function onMouseDown() {
		oneOff(_modal, "mouseup", ({ target }) => {
			if (target === _modal) {
				ignoreBackdropClick = true;
			}
		});
	}

	function onClick(e) {
		if (ignoreBackdropClick) {
			ignoreBackdropClick = false;

			return;
		}

		if (e.target !== e.currentTarget || options.backdrop !== true) {
			return;
		}

		typeof options.backdrop == "string" && options.backdrop == "static"
			? _modal.focus()
			: controls.hide();
	}

	function oneOff(element, event, callback) {
		function ev(e) {
			callback(e);
			element.removeEventListener(event, ev);
		}

		element.addEventListener(event, ev);
	}

	onDestroy(() => {
		controls.hide();
		document.removeEventListener("focusin", onFocusin);
		document.removeEventListener("focusin", onFocusin);
		window.removeEventListener("resize", adjustDialog);

		display = false;
		isShown = false;

		if (_backdrop) {
			document.body.classList.remove("modal-open");
			_backdrop.remove();
		}
	});
</script>

<div
	bind:this={_modal}
	class="modal fade"
	class:fade
	class:in={inClass}
	tabindex="-1"
	role="dialog"
	on:click={onClick}
	on:keydown={onKeydown}
	style={modalCSS.toString() || undefined}
>
	<div
		class="modal-dialog"
		class:modal-sm={options.size == "sm"}
		class:modal-lg={options.size == "lg"}
		role="document"
		bind:this={_dialog}
		on:mousedown={onMouseDown}
	>
		<div class="modal-content">
			<div class="modal-header">
				<button
					type="button"
					class="close"
					aria-label="Close"
					bind:this={_dismiss}
					on:click|preventDefault={controls.hide}
				>
					<span aria-hidden="true">&times;</span>
				</button>

				<h4 class="modal-title">
					{#if $$slots.title}
						<slot name="title" />
					{:else}
						{title}
					{/if}
				</h4>
			</div>

			<div class="modal-body">
				{#if $$slots.body}
					<slot name="body" />
				{:else}
					<slot>
						Please include some content for the body of this modal.
					</slot>
				{/if}
			</div>

			{#if $$slots.footer}
				<div class="modal-footer"><slot name="footer" /></div>
			{/if}
		</div>
	</div>
</div>
